/**
 * BannerController — bottom-left consent banner.
 *
 * Populates copy from server config, wires Accept/Decline/Customize buttons,
 * and shows itself on first visit (no stored choice).
 */

export default class BannerController {
	constructor({ root, store, copy, devMode = false, onOpenCustomize }) {
		this.root = root;
		this.store = store;
		this.copy = copy;
		this.devMode = devMode;
		this.onOpenCustomize = onOpenCustomize;
		this.suppressors = new Set();
		this.resumeAfterSuppression = false;
		this.hideTimer = 0;
	}

	init() {
		this._populate();
		this._wire();
		// Dev preview (admins only) always shows the banner, ignoring a saved choice.
		if (this.devMode || !this.store.hasChoice()) this.show();
		this.store.on('changed', () => this.hide());
	}

	_populate() {
		this._setText('[data-consent-title]', this.copy.title);
		// Body is authored in a WYSIWYG editor and cleaned with wp_kses_post()
		// before it is localized, so it carries safe inline markup: render it as
		// HTML, not plain text.
		this._setHtml('[data-consent-body]', this.copy.body);
		this._setText('[data-consent-accept]', this.copy.accept);
		this._setText('[data-consent-decline]', this.copy.decline);
		this._setText('[data-consent-customize]', this.copy.customize);
	}

	// A button may carry an inner [data-consent-label] span so a theme can
	// reshape it into a link-style element that needs its own label element;
	// write the text there when present, otherwise onto the element itself.
	// Buttons are optional (Decline / Customize can be hidden), so no-op if absent.
	_setText(selector, text) {
		const el = this.root.querySelector(selector);
		if (!el) return;
		const target = el.querySelector('[data-consent-label]') || el;
		target.textContent = text;
	}

	// Body copy only: server-sanitised WYSIWYG markup written as HTML.
	_setHtml(selector, html) {
		const el = this.root.querySelector(selector);
		if (!el) return;
		el.innerHTML = html || '';
	}

	_wire() {
		this.root.querySelector('[data-consent-accept]')?.addEventListener('click', () => {
			this.store.grant(['analytics', 'marketing']);
		});
		this.root.querySelector('[data-consent-decline]')?.addEventListener('click', () => {
			this.store.grant([]); // necessary only
		});
		this.root.querySelector('[data-consent-customize]')?.addEventListener('click', () => {
			this.onOpenCustomize();
		});
	}

	show() {
		if (this.suppressors.size) {
			this.resumeAfterSuppression = true;
			return;
		}
		window.clearTimeout(this.hideTimer);
		this.root.hidden = false;
		this.root.querySelector('[data-consent-banner]').dataset.state = 'open';
	}

	hide({notify = true} = {}) {
		this.resumeAfterSuppression = false;
		this.root.querySelector('[data-consent-banner]').dataset.state = 'closed';
		window.clearTimeout(this.hideTimer);
		this.hideTimer = window.setTimeout(() => {
			this.root.hidden = true;
			if (!notify) return;
			// The banner has finished closing and freed the bottom-bar slot.
			// Signal any feature that shares that slot (e.g. the Reading Bar)
			// that it may now appear. A plain DOM CustomEvent keeps the contract
			// loose, so a consumer needs no handle on window.jkd_consent.
			document.dispatchEvent(new CustomEvent('jkd:consent:dismissed'));
		}, 200); // match SCSS animation duration
	}

	/** Temporarily yield the bottom slot without recording a consent choice. */
	suspend(owner = 'anonymous') {
		if (this.suppressors.has(owner)) return;
		if (!this.suppressors.size) this.resumeAfterSuppression = this.isVisible();
		this.suppressors.add(owner);
		this.root.querySelector('[data-consent-banner]').dataset.state = 'closed';
		window.clearTimeout(this.hideTimer);
		this.hideTimer = window.setTimeout(() => {
			if (this.suppressors.size) this.root.hidden = true;
		}, 200);
	}

	/** Restore the banner only when it occupied the slot before suspension. */
	resume(owner = 'anonymous') {
		this.suppressors.delete(owner);
		if (this.suppressors.size || !this.resumeAfterSuppression) return;
		this.resumeAfterSuppression = false;
		if (this.devMode || !this.store.hasChoice()) this.show();
	}

	// Whether the banner is currently on screen. Lets a slot-sharing feature
	// decide, at boot, whether to defer its own appearance until dismissal.
	isVisible() {
		if (this.root.hidden) return false;
		const banner = this.root.querySelector('[data-consent-banner]');
		return !!banner && banner.dataset.state === 'open';
	}
}
